/**
 * MusicalInstrumentsServices - Delivery + optional Finance for high-value gear.
 *
 * Two panels:
 *   a. Delivery & Handling - piano specialists, fine-art couriers, standard mail
 *   b. Instrument Finance (shown only when country is UK AND price >= £2000)
 *      Three UK-recognised loan/instalment providers:
 *        - Take It Away Scheme (Arts Council 0% APR up to £5000, 12 months)
 *        - V12 Retail Finance (0-19.9% APR, up to 60 months)
 *        - Klarna Pay-in-3 (0% APR, 3 monthly instalments)
 *
 * Every card carries a DEMO badge until real partners sign up.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import {
  Truck, CreditCard, Piano, Guitar, Phone, MapPin, Clock, Star,
  AlertCircle, ShieldCheck, PoundSterling,
} from 'lucide-react';

const DELIVERY_DEMOS_UK = [
  {
    id: 'mi-del-1', name: 'Cadogan Tate - Fine Art & Instrument Specialist',
    type: 'White-Glove Specialist',
    tagline: 'Museum-grade handling for vintage & rare instruments (up to £250k insured)',
    rating: 4.9, reviews: 512, phone: '020 8965 4488',
    coverage: 'UK-wide, climate-controlled trucks',
    tags: ['Climate-Controlled', 'White-Glove', '£250k Insurance'],
    price_from: 180, eta: '3-5 working days',
    icon: Piano,
  },
  {
    id: 'mi-del-2', name: 'Panther Piano Movers',
    type: 'Piano Specialist',
    tagline: 'Upright, grand, digital - carried up stairs with piano boards & straps',
    rating: 4.8, reviews: 1240, phone: '0330 002 0800',
    coverage: 'UK mainland, 2-hr booking slot',
    tags: ['Piano Boards', 'Staircases', 'Uninsured pianos welcome'],
    price_from: 220, eta: 'Next-day / 2-day',
    icon: Piano,
  },
  {
    id: 'mi-del-3', name: 'UPS Standard - Guitars & Small Gear',
    type: 'Standard Courier',
    tagline: 'Guitars, brass, wind instruments in hard cases (up to 30kg)',
    rating: 4.6, reviews: 8420, phone: '03457 877 877',
    coverage: 'UK & EU, next-day option',
    tags: ['Tracked', 'Signature Required', 'Cover to £2500'],
    price_from: 18, eta: 'Next-day UK',
    icon: Guitar,
  },
];

const DELIVERY_DEMOS_SL = [
  {
    id: 'mi-del-1-sl', name: 'Kapruka Premium Handling',
    type: 'White-Glove Specialist',
    tagline: 'Careful handling for vintage & rare instruments (up to Rs 5M insured)',
    rating: 4.7, reviews: 320, phone: '011 483 6436',
    coverage: 'Islandwide, air-conditioned trucks',
    tags: ['Climate-Aware', 'White-Glove', 'Rs 5M Insurance'],
    price_from: 12500, eta: '2-4 working days',
    icon: Piano,
  },
  {
    id: 'mi-del-2-sl', name: 'Lanka Piano Movers',
    type: 'Piano Specialist',
    tagline: 'Upright, grand, digital - carried up stairs with piano boards & straps',
    rating: 4.8, reviews: 340, phone: '077 456 7890',
    coverage: 'Colombo, Kandy, Galle - 4-hr booking',
    tags: ['Piano Boards', 'Staircases', 'Trained crew'],
    price_from: 18500, eta: 'Next-day / 2-day',
    icon: Piano,
  },
  {
    id: 'mi-del-3-sl', name: 'Pronto Lanka - Guitars & Small Gear',
    type: 'Standard Courier',
    tagline: 'Guitars, brass, wind instruments in hard cases (up to 30kg)',
    rating: 4.6, reviews: 1820, phone: '011 231 8888',
    coverage: 'Islandwide, next-day option',
    tags: ['Tracked', 'Cash on Delivery', 'Cover to Rs 100k'],
    price_from: 850, eta: 'Next-day',
    icon: Guitar,
  },
];

const FINANCE_DEMOS = [
  {
    id: 'mi-fin-1', name: 'Take It Away - Arts Council Scheme',
    type: 'Interest-Free Loan',
    tagline: '0% APR over 12 months, up to £5000 - for musicians of any age (backed by Arts Council England)',
    rating: 4.8, reviews: 3420, phone: '0330 043 0140',
    coverage: 'UK residents, quick decision',
    tags: ['0% APR', 'Up to £5000', 'Arts Council backed'],
    website: 'https://takeitaway.org.uk',
    apr: '0%', min_term: '9-12 months', max_amount: 5000,
  },
  {
    id: 'mi-fin-2', name: 'V12 Retail Finance',
    type: 'Retail Instalment Plan',
    tagline: '0% APR or Low-APR options up to 60 months - for instruments £250-£25000',
    rating: 4.5, reviews: 5620, phone: '02920 468900',
    coverage: 'UK, 18+, soft credit check',
    tags: ['0-19.9% APR', 'Up to 60 months', 'FCA authorised'],
    website: 'https://www.v12retailfinance.com',
    apr: '0-19.9%', min_term: '6-60 months', max_amount: 25000,
  },
  {
    id: 'mi-fin-3', name: 'Klarna - Pay in 3',
    type: 'Buy-Now-Pay-Later',
    tagline: '0% APR over 3 months, no credit-score damage, instant approval',
    rating: 4.5, reviews: 8420, phone: null,
    coverage: 'UK, 18+, soft credit check',
    tags: ['0% APR', '3 Payments', 'No Fees'],
    website: 'https://www.klarna.com/uk/',
    apr: '0%', min_term: '3 months', max_amount: 1000,
  },
];

// ---- Shared provider card ----
const ProviderCard = ({ provider, isDemo, tone = 'orange', testIdPrefix, currencyPrefix = '£' }) => {
  const toneClasses = {
    orange: 'bg-orange-50 border-orange-200 text-orange-600',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
  };
  const Icon = provider.icon || CreditCard;
  return (
    <div
      className="relative border border-gray-200 rounded-lg p-4 hover:border-emerald-300 hover:shadow-sm transition-all bg-white"
      data-testid={`${testIdPrefix}-${provider.id}`}
    >
      {isDemo && (
        <div
          className="absolute top-0 right-0 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold px-2 py-0.5 rounded-bl"
          data-testid={`${testIdPrefix}-demo-${provider.id}`}
        >
          DEMO
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${toneClasses[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h4 className="font-semibold text-gray-900 text-sm">{provider.name}</h4>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{provider.type}</Badge>
          </div>
          <p className="text-xs text-gray-500 italic mt-0.5">{provider.tagline}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
            <span className="flex items-center gap-0.5 text-amber-600">
              <Star className="h-3 w-3 fill-current" />{provider.rating.toFixed(1)}
            </span>
            <span>({provider.reviews.toLocaleString()} reviews)</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-1.5 text-xs text-gray-600">
            {provider.coverage && (
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{provider.coverage}</span>
            )}
            {provider.eta && (
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{provider.eta}</span>
            )}
            {provider.apr && (
              <span className="flex items-center gap-1"><PoundSterling className="h-3 w-3" />{provider.apr} APR</span>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {provider.tags.map((tag) => (
              <span key={tag} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{tag}</span>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            {provider.price_from !== undefined ? (
              <span className="text-xs font-semibold text-emerald-700">From {currencyPrefix}{provider.price_from.toLocaleString()}</span>
            ) : (
              <span className="text-xs font-semibold text-emerald-700">
                {provider.min_term} • up to {currencyPrefix}{provider.max_amount.toLocaleString()}
              </span>
            )}
            {provider.phone ? (
              <Button
                size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => window.open(`tel:${provider.phone.replace(/\s/g, '')}`, '_self')}
                data-testid={`${testIdPrefix}-call-${provider.id}`}
              >
                <Phone className="h-3 w-3 mr-1" /> Call
              </Button>
            ) : (
              <Button
                size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => window.open(provider.website, '_blank', 'noopener')}
                data-testid={`${testIdPrefix}-visit-${provider.id}`}
              >
                Apply
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MusicalInstrumentsServices = ({ listing }) => {
  const isDemo = !!listing?.is_demo;
  const price = Number(listing?.price || 0);
  const country = (listing?.country || '').toUpperCase();
  const isSL = country === 'LK' || country === 'SL';
  const deliveryDemos = isSL ? DELIVERY_DEMOS_SL : DELIVERY_DEMOS_UK;
  const currencyPrefix = isSL ? 'Rs. ' : '£';
  // Financing only relevant for UK gear priced £2000+ (Take-It-Away scheme is UK-only)
  const showFinance = country === 'UK' && price >= 2000;

  return (
    <div className="space-y-6" data-testid="musical-instruments-services-stack">
      {isDemo && (
        <Alert className="border-amber-200 bg-amber-50" data-testid="musical-instruments-services-demo-notice">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800">
            <Badge variant="secondary" className="mr-2 bg-amber-100 text-amber-800 border-amber-200">DEMO</Badge>
            Delivery {showFinance ? 'and financing ' : ''}options below are illustrative for this demo listing.
            Live partners appear on real seller listings.
          </AlertDescription>
        </Alert>
      )}

      {/* a. Delivery */}
      <Card data-testid="music-delivery-section">
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Truck className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-lg">Delivery & Handling</CardTitle>
            <Badge variant="outline" className="text-xs">Specialist couriers</Badge>
            {isDemo && (
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid="music-delivery-demo-badge">DEMO</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            From concert-grade pianos to travel guitars - insured, tracked, climate-aware.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {deliveryDemos.map((p) => (
              <ProviderCard key={p.id} provider={p} isDemo={true} tone="orange" testIdPrefix="music-delivery-provider" currencyPrefix={currencyPrefix} />
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            <span>All partners carry insured goods-in-transit cover for instruments.</span>
          </div>
        </CardContent>
      </Card>

      {/* b. Finance - UK £2000+ */}
      {showFinance && (
        <Card data-testid="music-finance-section">
          <CardHeader>
            <div className="flex items-center gap-2 flex-wrap">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              <CardTitle className="text-lg">Spread the Cost</CardTitle>
              <Badge variant="outline" className="text-xs">Loan providers</Badge>
              {isDemo && (
                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid="music-finance-demo-badge">DEMO</Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Instruments over £2000 qualify for the Arts Council Take It Away scheme + other UK finance options.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {FINANCE_DEMOS.map((p) => (
                <ProviderCard key={p.id} provider={p} isDemo={true} tone="emerald" testIdPrefix="music-finance-provider" />
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
              <span>All lenders shown are FCA authorised - your consumer rights are unaffected.</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MusicalInstrumentsServices;
